import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Switch } from "../../ui/switch";
import { GET_AD_REQUESTS_QUERY_KEY } from "../../../utils/constants/queryKeys";
import toast from "react-hot-toast";
import AdsAPIs from "../../../apiRoutes/Ads";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import CustomModal from "../../common/CustomModal";

/** Auto launch switch with a confirmation; read-only for agencies and once a hook is live. */
const AutoLaunchCell = ({ request, canEdit }) => {
  const [pendingValue, setPendingValue] = useState(null);
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: ({ autoLaunch }) =>
      AdsAPIs.handleUpdateAutoLaunch({
        axiosPrivate,
        id: request.id,
        auto_launch: autoLaunch,
      }),
    onSuccess: (_data, { autoLaunch }) => {
      toast.success(`Auto launch turned ${autoLaunch ? "on" : "off"}`);
      setPendingValue(null);
      queryClient.invalidateQueries({ queryKey: GET_AD_REQUESTS_QUERY_KEY });
    },
  });
  const isOn = request.auto_launch !== false;
  const summary = request.launch_summary || {};
  const isLaunched = (summary.launched || 0) > 0;
  const label = isOn ? "On" : "Off";

  if (!canEdit || isLaunched) {
    return (
      <span
        className={isOn ? "text-gray-700" : "text-gray-400"}
        title={isLaunched ? "Locked once the ad set is live" : undefined}
      >
        {label}
      </span>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <Switch
          checked={isOn}
          disabled={isLoading}
          onCheckedChange={(next) => setPendingValue(next)}
          aria-label="Auto launch"
        />
        <span className="text-sm text-gray-700">{label}</span>
      </div>
      <CustomModal
        open={pendingValue !== null}
        title={pendingValue ? "Turn on auto launch?" : "Turn off auto launch?"}
        isLoading={isLoading}
        onClose={() => !isLoading && setPendingValue(null)}
        handleConfirmation={() => mutate({ autoLaunch: pendingValue })}
        handleConfirmationBtnText={pendingValue ? "Turn on" : "Turn off"}
      >
        <div className="space-y-2 whitespace-normal py-2 text-sm text-gray-700">
          <p
            className="truncate font-medium text-gray-900"
            title={request.adset_name}
          >
            {request.adset_name || `Request #${request.id}`}
          </p>
          <p>
            {pendingValue
              ? "AMS will schedule this ad set on Meta to go live at 12 AM on its launch date."
              : "If it is already scheduled on Meta, AMS takes it off. It won't launch automatically; someone will need to launch it by hand."}
          </p>
          <p className="text-xs text-gray-500">
            The change is logged with your name and the time.
          </p>
        </div>
      </CustomModal>
    </>
  );
};

export default AutoLaunchCell;
